import { DYClient, readDYIdentity } from '@theme/dy-core';

/**
 * @typedef {'dy' | 'manual'} TermsSource
 */

/**
 * <dy-search-box> — full-viewport search overlay driven by Dynamic Yield. Two keyword-only
 * lists are rendered (no product cards):
 *   • Trending — DY Autosuggest `querySuggestions` for an empty query ("hot searches"), shown
 *     on open and again whenever the input is cleared back to empty. These are popularity-driven
 *     query phrases from `/v2/serve/user/suggest`. Hidden (not trending, not autocomplete) while
 *     the typed term is 1-#minChars-1 characters — see #onInput.
 *   • Autocomplete — as-you-type product matches from DY Experience Search
 *     (`DYClient.autocomplete` → `/v2/serve/user/search`), rendered as their product titles.
 *     Falls back to Shopify's predictive search endpoint then the manual list if DY has no
 *     signal. Experience Search is used here (not Autosuggest) because `querySuggestions` only
 *     surfaces established popular phrases, so it never matches a product name that has yet to
 *     become a popular query.
 *
 * Selecting a term fills the input and submits the form, navigating to `/search?q=term`
 * (the DY-powered search results page handles the query natively).
 *
 * Config + curated terms arrive via `data-*` attributes; see {@link connectedCallback}.
 *
 * @element dy-search-box
 */
class DySearchBox extends HTMLElement {
  /** Debounce for as-you-type requests — one fetch per settled keystroke burst. */
  static #DEBOUNCE_MS = 100;

  /** Products requested per autocomplete keystroke — kept small so the response is quick. */
  static #PRODUCT_SUGGEST_SIZE = 5;

  /** @type {AbortController | null} Aborts in-flight DY suggest requests on newer keystrokes. */
  #dyAbortController = null;

  /** @type {AbortController | null} Aborts in-flight Shopify suggest requests on newer keystrokes. */
  #shopifyAbortController = null;

  /** @type {MutationObserver | null} Watches the host dialog's `open` attribute to lazy-load trending. */
  #openObserver = null;

  /** @type {number | undefined} Pending debounce timer id. */
  #debounceTimer;

  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLElement | null} */
  #list = null;

  /** @type {HTMLElement | null} Visually-hidden live region for screen reader announcements. */
  #liveRegion = null;

  /** @type {HTMLFormElement | null} The search form — submitting it with an empty term is blocked. */
  #form = null;

  /** @type {HTMLElement | null} Visible error shown when the form is submitted with an empty term. */
  #errorEl = null;

  /** @type {HTMLElement | null} The dialog whose `open` attribute gates the first trending load. */
  #dialog = null;

  /** True once trending has been requested for this open cycle, so re-renders never refetch it. */
  #trendingLoaded = false;

  /** True while trending terms are the currently displayed list (not autocomplete results). */
  #trendingShown = false;

  /** Index of the keyboard-highlighted option, or -1 when none is highlighted. */
  #activeIndex = -1;

  /** @type {DYClient | null} Experience Search client for as-you-type product matches. */
  #client = null;

  /**
   * Monotonic render token shared by every async list load (trending and autocomplete). Neither
   * `DYClient.serve` nor the trending `/suggest` fetch can be reliably cancelled mid-flight, so a
   * load bumps this token, captures the value, and only renders if it is still the latest — a
   * superseded load (e.g. a slow autocomplete resolving after the user cleared back to trending)
   * is discarded. Must be incremented by ALL load paths, or a stale one can clobber a newer list.
   */
  #renderSeq = 0;

  connectedCallback() {
    this.#dyAbortController?.abort();
    this.#dyAbortController = new AbortController();
    this.#shopifyAbortController?.abort();
    this.#shopifyAbortController = new AbortController();

    this.#client = this.dataset.apiKey
      ? new DYClient({
          apiKey: this.dataset.apiKey,
          dataCenter: this.dataset.dataCenter === 'eu' ? 'eu' : 'com',
          storeUrl: this.dataset.storeUrl,
          locale: this.dataset.locale || '',
          inStockOnly: this.dataset.inStockOnly === 'true',
          // Terms-only UI: we render product titles, so ask DY for just the `name` column
          // (a real feed column) to keep the autocomplete payload light.
          fields: ['name'],
        })
      : null;

    this.#input = this.querySelector('.dy-search-box__input');
    this.#list = this.querySelector('.dy-search-box__list');
    this.#liveRegion = this.querySelector('[data-live-region]');
    this.#errorEl = this.querySelector('.dy-search-box__error');
    this.#dialog = this.closest('dialog');

    if (!this.#input || !this.#list) return;

    this.#form = this.#input.form;

    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('keydown', this.#onKeyDown);
    this.#list.addEventListener('click', this.#onListClick);
    this.#form?.addEventListener('submit', this.#onSubmit);

    // Trending loads lazily the first time the dialog opens, not on every page load.
    if (this.#dialog) {
      this.#openObserver = new MutationObserver(() => {
        if (this.#dialog?.hasAttribute('open')) {
          this.#onOpen();
        } else {
          // Invalidate any in-flight load AND cancel a pending debounce so neither a late
          // response nor a not-yet-fired keystroke can render into the closed dialog.
          this.#renderSeq++;
          clearTimeout(this.#debounceTimer);
          this.#trendingLoaded = false;
          this.#trendingShown = false;
          this.#list?.replaceChildren();
          if (this.#input) this.#input.value = '';
          this.#input?.setAttribute('aria-expanded', 'false');
          this.#input?.removeAttribute('aria-activedescendant');
          if (this.#liveRegion) this.#liveRegion.textContent = '';
          this.#hideError();
        }
      });
      this.#openObserver.observe(this.#dialog, { attributes: true, attributeFilter: ['open'] });
      if (this.#dialog.hasAttribute('open')) this.#onOpen();
    } else {
      this.#onOpen();
    }
  }

  disconnectedCallback() {
    this.#dyAbortController?.abort();
    this.#shopifyAbortController?.abort();
    this.#openObserver?.disconnect();
    clearTimeout(this.#debounceTimer);
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('keydown', this.#onKeyDown);
    this.#list?.removeEventListener('click', this.#onListClick);
    this.#form?.removeEventListener('submit', this.#onSubmit);
  }

  /** Minimum characters to show autocomplete results — see #onInput for the hidden gap in between. */
  get #minChars() {
    return Number(this.dataset.minChars) || 3;
  }

  /** Maximum terms to request/render. */
  get #maxTerms() {
    return Number(this.dataset.maxTerms) || 8;
  }

  /** 'dy' (Autosuggest → Shopify → manual fallback chain) or 'manual' (Shopify autocomplete, manual fallback). */
  get #termsSource() {
    return this.dataset.termsSource === 'manual' ? 'manual' : 'dy';
  }

  /** Admin-curated terms, parsed from the JSON array in `data-manual-terms`. */
  get #manualTerms() {
    try {
      const parsed = JSON.parse(this.dataset.manualTerms || '[]');
      return Array.isArray(parsed) ? parsed.map((t) => String(t).trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }

  /** Load trending terms once per open cycle and focus the input. */
  #onOpen() {
    if (this.#trendingLoaded) return;
    this.#trendingLoaded = true;
    this.#loadTrending();
    // Defer focus until the dialog's open transition has committed.
    requestAnimationFrame(() => this.#input?.focus());
  }

  /** @param {string[]} terms */
  #renderTerms(terms) {
    if (!this.#list) return;
    this.#activeIndex = -1;
    this.#input?.removeAttribute('aria-activedescendant');

    if (terms.length === 0) {
      this.#list.replaceChildren();
      this.#input?.setAttribute('aria-expanded', 'false');
      if (this.#liveRegion) this.#liveRegion.textContent = '';
      return;
    }

    const items = terms.slice(0, this.#maxTerms).map((term, index) => {
      const li = document.createElement('li');
      li.className = 'dy-search-box__option';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.id = `dy-search-box-option-${index}`;
      li.dataset.term = term;
      li.tabIndex = -1;
      li.textContent = term;
      return li;
    });

    this.#list.replaceChildren(...items);
    this.#input?.setAttribute('aria-expanded', 'true');

    const count = Math.min(terms.length, this.#maxTerms);
    if (this.#liveRegion) this.#liveRegion.textContent = `${count} suggestion${count === 1 ? '' : 's'} available`;
  }

  #onInput = () => {
    clearTimeout(this.#debounceTimer);
    this.#hideError();
    const text = this.#input?.value.trim() ?? '';

    if (text.length === 0) {
      // Cleared back to the initial/opened state — show trending again.
      if (!this.#trendingShown) this.#loadTrending();
      return;
    }

    if (text.length < this.#minChars) {
      // Below the search threshold but not empty — hide the list entirely while the
      // user keeps typing, rather than leaving trending (or stale results) showing.
      this.#trendingShown = false;
      this.#renderSeq++;
      this.#renderTerms([]);
      return;
    }

    this.#debounceTimer = window.setTimeout(() => this.#loadAutocomplete(text), DySearchBox.#DEBOUNCE_MS);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const options = this.#list ? [...this.#list.querySelectorAll('.dy-search-box__option')] : [];

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (options.length === 0) return;
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      // Clamp per ARIA combobox 1.2 — no wrapping at boundaries.
      this.#activeIndex = Math.max(0, Math.min(this.#activeIndex + delta, options.length - 1));
      this.#highlight(options);
      return;
    }

    // First Escape clears the highlight; second Escape bubbles to close the dialog.
    if (event.key === 'Escape' && this.#activeIndex !== -1) {
      event.stopPropagation();
      this.#activeIndex = -1;
      this.#highlight(options);
      return;
    }

    if (event.key === 'Enter') {
      const active = options[this.#activeIndex];
      const term = active instanceof HTMLElement ? active.dataset.term : undefined;
      if (term) {
        event.preventDefault();
        this.#submit(term);
      }
      // No highlighted option: let the form submit the typed query natively.
    }
  };

  /** Block submitting a blank search term; show an inline error instead of navigating to `/search`. */
  #onSubmit = (event) => {
    if ((this.#input?.value.trim() ?? '').length > 0) {
      this.#hideError();
      return;
    }

    event.preventDefault();
    this.#showError();
  };

  /** @param {Element[]} options */
  #highlight(options) {
    options.forEach((option, index) => {
      const isActive = index === this.#activeIndex;
      option.classList.toggle('dy-search-box__option--active', isActive);
      option.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    const activeId = options[this.#activeIndex]?.id;
    if (activeId) this.#input?.setAttribute('aria-activedescendant', activeId);
    else this.#input?.removeAttribute('aria-activedescendant');
  }

  /** @param {MouseEvent} event */
  #onListClick = (event) => {
    const option = /** @type {HTMLElement} */ (event.target).closest('.dy-search-box__option');
    const term = option instanceof HTMLElement ? option.dataset.term : undefined;
    if (term) this.#submit(term);
  };

  /** Fill the input with the chosen term and submit the form → `/search?q=term`. */
  #submit(term) {
    if (!this.#input) return;
    this.#input.value = term;
    this.#form?.requestSubmit();
  }

  #showError() {
    if (!this.#errorEl) return;
    this.#errorEl.hidden = false;
    // Force a genuine text mutation so screen readers re-announce even when the error
    // is already visible (e.g. two blank submits in a row) — toggling `hidden` alone
    // is a no-op in that case and most ARIA live regions won't re-fire on it.
    this.#errorEl.textContent = this.#errorEl.textContent;
    this.#input?.setAttribute('aria-invalid', 'true');
  }

  #hideError() {
    if (!this.#errorEl) return;
    this.#errorEl.hidden = true;
    this.#input?.removeAttribute('aria-invalid');
  }

  /**
   * Load trending terms and render them, discarding the result if a newer load (trending or
   * autocomplete) has since started — otherwise a slow autocomplete resolving after the user
   * cleared back below `minChars` could overwrite this trending list, or vice versa.
   */
  async #loadTrending() {
    this.#trendingShown = true;
    const seq = ++this.#renderSeq;
    const terms = await this.#resolveTrendingTerms();
    if (seq !== this.#renderSeq) return;
    this.#renderTerms(terms);
  }

  /**
   * Fallback for trending terms. `dy` source: DY Autosuggest `querySuggestions` → manual list.
   * `manual` source: the curated list directly. Always resolves (never throws) so
   * {@link #loadTrending} can render once.
   *
   * @returns {Promise<string[]>}
   */
  async #resolveTrendingTerms() {
    if (this.#termsSource === 'manual') return this.#manualTerms;
    try {
      const terms = await this.#requestSuggestions('');
      return terms.length > 0 ? terms : this.#manualTerms;
    } catch {
      return this.#manualTerms;
    }
  }

  /**
   * Resolve the term list for a keystroke and render it, discarding the result if a newer load
   * has since started (guards against out-of-order DY responses).
   *
   * @param {string} text
   */
  async #loadAutocomplete(text) {
    this.#trendingShown = false;
    const seq = ++this.#renderSeq;
    const terms = await this.#resolveAutocompleteTerms(text);
    if (seq !== this.#renderSeq) return;
    this.#renderTerms(terms);
  }

  /**
   * Fallback chain for as-you-type terms. `dy` source: DY Experience Search product titles →
   * Shopify predictive queries → manual list. `manual` source: Shopify predictive queries →
   * manual list. Always resolves (never throws) so {@link #loadAutocomplete} can render once.
   *
   * @param {string} text
   * @returns {Promise<string[]>}
   */
  async #resolveAutocompleteTerms(text) {
    if (this.#termsSource === 'manual') {
      try {
        const terms = await this.#requestShopifySuggestions(text);
        return terms.length > 0 ? terms : this.#filterManual(text);
      } catch {
        return this.#filterManual(text);
      }
    }
    // Isolate the DY call: a DY error (not just an empty result) must still fall through to the
    // Shopify predictive step rather than skipping straight to the manual list.
    let terms = await this.#requestProductTitles(text).catch(() => []);
    if (terms.length === 0) {
      try {
        terms = await this.#requestShopifySuggestions(text);
      } catch {
        return this.#filterManual(text);
      }
    }
    return terms.length > 0 ? terms : this.#filterManual(text);
  }

  /**
   * Query DY Experience Search for products matching the typed text and return their titles
   * (`slot.productData.name`), de-duplicated across colourway variants. This is the same
   * semantic engine as the search results page, so it matches the catalog rather than
   * popularity-ranked query phrases.
   *
   * @param {string} text
   * @returns {Promise<string[]>}
   */
  async #requestProductTitles(text) {
    if (!this.#client) return [];
    const result = await this.#client.autocomplete(text, DySearchBox.#PRODUCT_SUGGEST_SIZE);
    const slots = result?.slots ?? [];
    const titles = slots.map((slot) => String(slot?.productData?.name ?? '').trim()).filter(Boolean);
    return [...new Set(titles)].slice(0, this.#maxTerms);
  }

  /** Prefix-match the curated list, so autocomplete works even with zero DY/Shopify signal. */
  #filterManual(text) {
    const needle = text.toLowerCase();
    return this.#manualTerms.filter((term) => term.toLowerCase().includes(needle));
  }

  /**
   * POST to the DY Autosuggest endpoint and return the keyword strings. Rejects on a stale
   * request (aborted by a newer keystroke) or a non-OK response, so callers fall back.
   *
   * @param {string} text
   * @returns {Promise<string[]>}
   */
  async #requestSuggestions(text) {
    if (!this.dataset.apiKey) return [];

    this.#dyAbortController?.abort();
    this.#dyAbortController = new AbortController();

    const host = `https://direct.dy-api.${this.dataset.dataCenter === 'eu' ? 'eu' : 'com'}`;
    const res = await fetch(`${host}/v2/serve/user/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'DY-API-Key': this.dataset.apiKey },
      body: JSON.stringify({
        ...readDYIdentity(),
        query: { text, suggestions: [{ type: 'querySuggestions', maxResults: this.#maxTerms }] },
        context: { page: { locale: this.dataset.locale || 'en_AU' } },
      }),
      signal: this.#dyAbortController.signal,
    });

    if (!res.ok) throw new Error(`DY suggest failed (${res.status})`);
    return this.#parseSuggestions(await res.json());
  }

  /**
   * Fetch query suggestions from Shopify's predictive search endpoint (same-origin, no CORS).
   * Returns keyword strings from `resources.results.queries[].text`.
   *
   * @param {string} text
   * @returns {Promise<string[]>}
   */
  async #requestShopifySuggestions(text) {
    this.#shopifyAbortController?.abort();
    this.#shopifyAbortController = new AbortController();

    const url = `/search/suggest.json?q=${encodeURIComponent(text)}&resources[type]=query&resources[limit]=${this.#maxTerms}`;
    const res = await fetch(url, { signal: this.#shopifyAbortController.signal });

    if (!res.ok) throw new Error(`Shopify suggest failed (${res.status})`);
    const json = await res.json();
    const queries = /** @type {any} */ (json)?.resources?.results?.queries;
    if (!Array.isArray(queries)) return [];
    return queries.map((q) => String(q?.text ?? '').trim()).filter(Boolean);
  }

  /**
   * Extract keyword strings from the suggest envelope
   * (`variations[0].payload.data.suggestions.querySuggestions`). Each item is an object with a
   * `term` key (e.g. `{ term: 'umbrellas' }`); the remaining keys are kept as defensive
   * fallbacks in case DY returns a different shape for autocomplete vs trending.
   *
   * @param {unknown} json
   * @returns {string[]}
   */
  #parseSuggestions(json) {
    const items = /** @type {any} */ (json)?.variations?.[0]?.payload?.data?.suggestions?.querySuggestions;
    if (!Array.isArray(items)) return [];
    return items
      .map((item) =>
        typeof item === 'string'
          ? item
          : (item?.term ?? item?.text ?? item?.title ?? item?.value ?? item?.query ?? item?.name ?? ''),
      )
      .map((term) => String(term).trim())
      .filter(Boolean);
  }
}

if (!customElements.get('dy-search-box')) {
  customElements.define('dy-search-box', DySearchBox);
}
